import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, FileText } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCredits } from "@/hooks/useCredits";

interface Resume {
  id: string;
  version_name: string | null;
  ats_score_after: number | null;
  created_at: string;
  source_type: string;
}

export default function TailorToJob() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { credits, refetch: refetchCredits } = useCredits();
  
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string>("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingResumes, setIsLoadingResumes] = useState(true);

  useEffect(() => {
    loadResumes();
    
    // Pre-fill if coming from job page
    const jobId = searchParams.get("jobId");
    if (jobId) {
      loadJobDetails(jobId);
    }
  }, [searchParams]);

  const loadResumes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("optimized_resumes")
        .select("id, version_name, ats_score_after, created_at, source_type, is_default")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setResumes(data || []);
      
      // Select default resume if exists
      const defaultResume = data?.find(r => r.is_default);
      if (defaultResume) {
        setSelectedResumeId(defaultResume.id);
      }
    } catch (error: any) {
      console.error("Error loading resumes:", error);
      toast({
        title: "Error",
        description: "Failed to load your resumes",
        variant: "destructive",
      });
    } finally {
      setIsLoadingResumes(false);
    }
  };

  const loadJobDetails = async (jobId: string) => {
    try {
      const { data, error } = await supabase
        .from("jobs")
        .select("title, company, raw")
        .eq("id", jobId)
        .single();

      if (error) throw error;
      if (data) {
        setJobTitle(data.title || "");
        setCompanyName(data.company || "");
        const rawData = data.raw as Record<string, any>;
        setJobDescription(rawData?.description || "");
      }
    } catch (error) {
      console.error("Error loading job:", error);
    }
  };

  const handleTailorResume = async () => {
    if (!selectedResumeId) {
      toast({
        title: "Select Resume",
        description: "Please select a resume to tailor",
        variant: "destructive",
      });
      return;
    }

    if (!jobDescription.trim()) {
      toast({
        title: "Missing Job Description",
        description: "Please paste the job description",
        variant: "destructive",
      });
      return;
    }

    if ((credits?.remaining || 0) < 1) {
      toast({
        title: "Insufficient Credits",
        description: "You need 1 credit to tailor your resume",
        variant: "destructive",
      });
      navigate("/pricing");
      return;
    }

    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("tailor-resume-to-job", {
        body: {
          userId: user.id,
          resumeId: selectedResumeId,
          jobDescription: jobDescription.trim(),
          jobTitle: jobTitle.trim(),
          companyName: companyName.trim(),
        },
      });

      if (error) throw error;

      await refetchCredits();

      toast({
        title: "Resume Tailored!",
        description: "Your resume has been optimized for this job",
      });

      navigate(`/app/resume/optimize?resumeId=${data.optimizedResumeId}`);
    } catch (error: any) {
      console.error("Error tailoring resume:", error);
      toast({
        title: "Tailoring Failed",
        description: error.message || "Failed to tailor resume",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingResumes) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Tailor Resume to Job
          </h1>
          <p className="text-muted-foreground">
            Optimize your resume for a specific job posting
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Select Resume */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Step 1: Select Base Resume
            </h2>
            
            {resumes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  You don't have any resumes yet
                </p>
                <Button onClick={() => navigate("/app/resume/create")}>
                  Create Resume
                </Button>
              </div>
            ) : (
              <RadioGroup value={selectedResumeId} onValueChange={setSelectedResumeId}>
                <div className="space-y-3">
                  {resumes.map((resume) => (
                    <div
                      key={resume.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/5 transition-colors"
                    >
                      <RadioGroupItem value={resume.id} id={resume.id} />
                      <Label
                        htmlFor={resume.id}
                        className="flex-1 cursor-pointer flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium">
                            {resume.version_name || "Untitled Resume"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(resume.created_at).toLocaleDateString()} · 
                            {resume.ats_score_after && ` ATS Score: ${resume.ats_score_after}`}
                          </div>
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            )}
          </Card>

          {/* Step 2: Job Details */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Step 2: Job Details
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="jobTitle">Job Title (Optional)</Label>
                <input
                  id="jobTitle"
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Senior Software Engineer"
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                />
              </div>

              <div>
                <Label htmlFor="companyName">Company Name (Optional)</Label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., Google"
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                />
              </div>

              <div>
                <Label htmlFor="jobDescription">
                  Job Description <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="jobDescription"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the full job description here..."
                  rows={12}
                  className="mt-1"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Include the full job posting for best results
                </p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigate("/app/resume/library")}
            >
              Cancel
            </Button>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                Cost: <span className="font-semibold text-foreground">1 credit</span>
              </div>
              <Button
                onClick={handleTailorResume}
                disabled={isLoading || !selectedResumeId || !jobDescription.trim()}
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Tailoring Resume...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Tailor Resume
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
